import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { useTheme } from "@mui/material";
import {
  checkedDeviceListArray,
  FormattedMapPointTypes,
  mapDataPoints,
  MapDataPointTypes,
  MapDataTypes,
} from "depot/map-page.depot";
import moment from "moment";
import L from "leaflet";
import { CommonText, ErrorText } from "components/common/text/text.component";
import glowEffect from "assets/img/glow-effect.png";
import { getDateString, getColorMarker, getColorDot } from "utils/util-functions";
import {
  formatFetchedMapPoints,
  getDeviceMapPoints,
  getMappingHistoryExportPage,
  getMappingHistoryMetadata,
  getMappingHistoryWindow,
  MappingHistoryMetadata,
} from "api/MappingHelpers";
import { MAPBOX_STYLE_ID } from "utils/constants";

import { MapPageContainer, PageContent, PageTitle, RightWrapper, RouteToolsHelpContainer } from "./MapPage.style";
import { CENTER_POINT_LATITUDE, CENTER_POINT_LONGITUDE, DATA_FIELDS_STR } from "./Map.config";
import MapSidebar from "./components/MapSidebar/map-sidebar.component";
import { RouteTools } from "./components/route-tools/route-tools.component";
import {
  getInitialPointIndexRange,
  isValidPointTimeIndex,
  limitMapPoints,
  MAX_DISPLAY_POINTS,
  PointIndexRange,
} from "./map-history.utilities";
import {
  createMapHistoryCsvBlob,
  downloadBlob,
  fetchAllMappingHistory,
  fetchMappingHistoryWithRetry,
} from "./map-history-export.utilities";

interface RouteLayer {
  serialNumber: string;
  routeInstance: L.Polyline;
}

interface DownloadProgress {
  downloaded: number;
  total: number;
}

const getSafeDisplayLimit = (displayLimit: number): number =>
  Math.max(1, Math.min(displayLimit || MAX_DISPLAY_POINTS, MAX_DISPLAY_POINTS));

const MapPage = (): JSX.Element => {
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [deviceDataObj, setDeviceDataObj] = useState<Record<string, MapDataTypes | null>>({});
  const [selectedSerials, setSelectedSerials] = useState<string[]>(checkedDeviceListArray.data);
  const [showRoute, setShowRoute] = useState<boolean>(false);
  const [historyMetadata, setHistoryMetadata] = useState<MappingHistoryMetadata | null>(null);
  const [sliderRange, setSliderRange] = useState<PointIndexRange | null>(null);
  const [isWindowLoading, setIsWindowLoading] = useState<boolean>(false);
  const [historyError, setHistoryError] = useState<string>();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress>();
  const [downloadError, setDownloadError] = useState<string>();
  const { palette } = useTheme();
  const { sn } = useParams();

  const mapRef = useRef<L.Map>();
  const groupRef = useRef<L.FeatureGroup>();
  const routeLayersRef = useRef<RouteLayer[]>([]);
  const metadataAbortRef = useRef<AbortController>();
  const windowAbortRef = useRef<AbortController>();
  const exportAbortRef = useRef<AbortController>();
  const windowRequestIdRef = useRef(0);
  const exportRequestIdRef = useRef(0);
  const committedSliderRangeRef = useRef<PointIndexRange | null>(null);

  const deviceArray = useMemo<MapDataTypes[]>(
    () => Object.values(deviceDataObj).filter((item): item is MapDataTypes => Boolean(item)),
    [deviceDataObj],
  );
  const singleSelectedSerial = selectedSerials.length === 1 ? selectedSerials[0] : undefined;
  const singleDeviceHasLegacyResult = Boolean(
    singleSelectedSerial && Object.prototype.hasOwnProperty.call(deviceDataObj, singleSelectedSerial),
  );

  const historyBounds = useMemo<PointIndexRange | null>(() => {
    if (
      !historyMetadata ||
      historyMetadata.pointCount <= 0 ||
      historyMetadata.retainedFrom === null ||
      historyMetadata.retainedTo === null ||
      historyMetadata.retainedFrom > historyMetadata.retainedTo
    ) {
      return null;
    }

    return [0, historyMetadata.pointCount - 1];
  }, [historyMetadata]);

  const markerPopup = (
    serial: string,
    lat: number,
    lon: number,
    time: string,
    color: string | undefined,
    rsrp: number | undefined,
    isOffline: boolean,
  ) => `
    <div>
      <div>SN: ${serial}</div>
      <div>Date: ${getDateString(time)}</div>
      <div>Latitude: ${lat}</div>
      <div>Longitude: ${lon}</div>
      <div>Status: ${isOffline ? "Offline (Buffered)" : "Online"}</div>
      ${isOffline ? "" : `<div>RSRP: <span style="color: ${color}">${rsrp ?? "-"}</span></div>`}
    </div>
  `;

  const createCustomIcon = (index: number, color: string | undefined, serialNumber: string, isOffline: boolean) => {
    if (isOffline) {
      const size = index === 0 ? 18 : 14;
      return L.divIcon({
        className: "offline-breadcrumb-marker",
        html: `<div style="
          width: ${size}px;
          height: ${size}px;
          border-radius: 50%;
          border: 2px dashed #6E6E6E;
          background: rgba(174, 174, 174, 0.35);
          box-sizing: border-box;
        "></div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
        popupAnchor: [0, -size / 2],
      });
    }

    return index === 0
      ? L.icon({
          iconUrl: getColorMarker(color),
          shadowUrl: serialNumber === singleSelectedSerial ? glowEffect : undefined,
          shadowSize: [70, 70],
          shadowAnchor: [35, 60],
          iconSize: [15, 29],
          iconAnchor: [7, 29],
          popupAnchor: [0, -30],
        })
      : L.icon({
          iconUrl: getColorDot(color),
          shadowUrl: serialNumber === singleSelectedSerial ? glowEffect : undefined,
          shadowSize: [70, 70],
          shadowAnchor: [35, 35],
          iconSize: [15, 15],
          iconAnchor: [7, 7],
          popupAnchor: [0, -5],
        });
  };

  const loadHistoryWindow = useCallback(
    async (serialNumber: string, metadata: MappingHistoryMetadata, requestedRange: PointIndexRange) => {
      windowAbortRef.current?.abort();
      const controller = new AbortController();
      windowAbortRef.current = controller;
      const requestId = ++windowRequestIdRef.current;
      const displayLimit = getSafeDisplayLimit(metadata.displayLimit);

      setIsWindowLoading(true);
      setHistoryError(undefined);

      try {
        const history = await getMappingHistoryWindow({
          serialNumber,
          startIndex: requestedRange[0],
          endIndex: requestedRange[1],
          snapshot: metadata.snapshot,
          signal: controller.signal,
        });

        if (controller.signal.aborted || requestId !== windowRequestIdRef.current) {
          return;
        }

        if (
          history.startIndex !== requestedRange[0] ||
          history.endIndex !== requestedRange[1] ||
          history.truncated ||
          history.dataPoints.length !== requestedRange[1] - requestedRange[0] + 1 ||
          !Number.isFinite(history.from) ||
          !Number.isFinite(history.to) ||
          history.from > history.to ||
          history.from !== metadata.pointTimes[requestedRange[0]] ||
          history.to !== metadata.pointTimes[requestedRange[1]]
        ) {
          throw new Error("The map history service returned an invalid window.");
        }

        const chronologicalPoints = limitMapPoints(
          [...history.dataPoints].sort((first, second) => first.timeStamp - second.timeStamp),
          displayLimit,
        );
        const formattedData = formatFetchedMapPoints(
          { mappingData: [{ device: history.device, dataPoints: chronologicalPoints }] },
          displayLimit,
        )[0];

        mapDataPoints.setData({ sn: serialNumber, mapData: formattedData });
        committedSliderRangeRef.current = requestedRange;
      } catch {
        if (!controller.signal.aborted && requestId === windowRequestIdRef.current) {
          if (committedSliderRangeRef.current) {
            setSliderRange(committedSliderRangeRef.current);
          }
          setHistoryError("Failed to load the selected map history.");
        }
      } finally {
        if (requestId === windowRequestIdRef.current) {
          setIsWindowLoading(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    const mapDepotData = mapDataPoints.observable.subscribe({
      next: (depotData: MapDataPointTypes) => {
        if (!depotData.sn) {
          return;
        }

        setDeviceDataObj((current) => {
          const next = { ...current };
          if (depotData.mapData) {
            next[depotData.sn] = depotData.mapData;
          } else {
            delete next[depotData.sn];
          }
          return next;
        });
      },
    });
    const selectedDevices = checkedDeviceListArray.observable.subscribe({ next: setSelectedSerials });

    if (sn) {
      checkedDeviceListArray.setData([sn]);
      getDeviceMapPoints(sn, DATA_FIELDS_STR, true).catch(() => {});
    }

    if (!mapRef.current) {
      mapRef.current = L.map("map-container").setView([CENTER_POINT_LONGITUDE, CENTER_POINT_LATITUDE], 5);
      L.tileLayer(
        `https://api.mapbox.com/styles/v1/mapbox/${MAPBOX_STYLE_ID}/tiles/{z}/{x}/{y}?access_token=${process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}`,
        {
          attribution: 'Imagery &copy; <a href="https://www.mapbox.com/">Mapbox</a>',
          maxZoom: 19,
        },
      ).addTo(mapRef.current);
      groupRef.current = L.featureGroup().addTo(mapRef.current);
    }

    return () => {
      window.sessionStorage.removeItem("activeDevice");
      mapDepotData.unsubscribe();
      selectedDevices.unsubscribe();
      metadataAbortRef.current?.abort();
      windowAbortRef.current?.abort();
      exportAbortRef.current?.abort();
      mapRef.current?.remove();
      mapRef.current = undefined;
      groupRef.current = undefined;
    };
  }, []);

  useEffect(() => {
    metadataAbortRef.current?.abort();
    windowAbortRef.current?.abort();
    exportAbortRef.current?.abort();
    windowRequestIdRef.current += 1;
    exportRequestIdRef.current += 1;
    setHistoryMetadata(null);
    setSliderRange(null);
    committedSliderRangeRef.current = null;
    setIsWindowLoading(false);
    setHistoryError(undefined);
    setDownloadError(undefined);
    setDownloadProgress(undefined);
    setIsDownloading(false);

    if (!singleSelectedSerial || !singleDeviceHasLegacyResult) {
      return undefined;
    }

    const controller = new AbortController();
    metadataAbortRef.current = controller;

    const loadMetadata = async () => {
      try {
        const metadata = await getMappingHistoryMetadata(singleSelectedSerial, controller.signal);
        if (controller.signal.aborted) {
          return;
        }

        if (!isValidPointTimeIndex(metadata)) {
          throw new Error("The map history service returned invalid metadata.");
        }

        setHistoryMetadata(metadata);
        if (metadata.pointCount === 0) {
          mapDataPoints.setData({ sn: singleSelectedSerial, mapData: { sn: singleSelectedSerial, locations: [] } });
          return;
        }

        const initialRange = getInitialPointIndexRange(metadata.pointCount, getSafeDisplayLimit(metadata.displayLimit));
        if (!initialRange) {
          return;
        }
        setSliderRange(initialRange);
        await loadHistoryWindow(singleSelectedSerial, metadata, initialRange);
      } catch {
        if (!controller.signal.aborted) {
          setHistoryError("Failed to load map history.");
        }
      }
    };

    loadMetadata();
    return () => controller.abort();
  }, [loadHistoryWindow, singleDeviceHasLegacyResult, singleSelectedSerial]);

  useEffect(() => {
    if (!mapRef.current || !groupRef.current) {
      return;
    }

    routeLayersRef.current.forEach(({ routeInstance }) => mapRef.current?.removeLayer(routeInstance));
    routeLayersRef.current = [];
    groupRef.current.clearLayers();

    deviceArray.forEach(({ locations, sn: serialNumber }) => {
      const renderedLocations = limitMapPoints(locations);
      if (renderedLocations.length > 1) {
        const routeInstance = L.polyline(
          renderedLocations.map(({ lat, lon }) => L.latLng(lat, lon)),
          serialNumber === singleSelectedSerial ? { color: "#EA4335", weight: 5 } : { color: "#6FA1EC", weight: 3 },
        );
        if (showRoute) {
          routeInstance.addTo(mapRef.current as L.Map);
        }
        routeLayersRef.current.push({ serialNumber, routeInstance });
      }

      renderedLocations.forEach(({ time, rsrp, lat, lon, color, isOffline }: FormattedMapPointTypes, index: number) => {
        const marker = L.marker([lat, lon], {
          icon: createCustomIcon(index, color, serialNumber, isOffline),
          title: `${index + 1}`,
        }).addTo(groupRef.current as L.FeatureGroup);
        const popupContent = markerPopup(serialNumber, lat, lon, time, color, rsrp, isOffline);

        marker.on("click", () => {
          mapRef.current?.flyTo([lat, lon], mapRef.current.getZoom(), { animate: true, duration: 1 });
          marker.unbindPopup().bindPopup(popupContent).openPopup();
        });
      });
    });

    const bounds = groupRef.current.getBounds();
    if (bounds.isValid()) {
      mapRef.current.fitBounds(bounds);
    }
  }, [deviceArray, showRoute, singleSelectedSerial]);

  const handleSliderChange = (range: PointIndexRange) => {
    setSliderRange(range);
  };

  const handleSliderCommit = (range: PointIndexRange) => {
    if (!historyBounds || !historyMetadata || !singleSelectedSerial) {
      return;
    }

    const selectedPointCount = range[1] - range[0] + 1;
    const displayLimit = getSafeDisplayLimit(historyMetadata.displayLimit);
    if (
      range[0] < historyBounds[0] ||
      range[1] > historyBounds[1] ||
      range[0] > range[1] ||
      selectedPointCount > displayLimit
    ) {
      return;
    }

    setSliderRange(range);
    if (committedSliderRangeRef.current?.[0] === range[0] && committedSliderRangeRef.current?.[1] === range[1]) {
      return;
    }
    loadHistoryWindow(singleSelectedSerial, historyMetadata, range);
  };

  const downloadMapHistory = async () => {
    if (!singleSelectedSerial || isDownloading) {
      return;
    }

    exportAbortRef.current?.abort();
    const controller = new AbortController();
    exportAbortRef.current = controller;
    const requestId = ++exportRequestIdRef.current;
    setIsDownloading(true);
    setDownloadError(undefined);
    setDownloadProgress(undefined);

    try {
      const completeHistory = await fetchMappingHistoryWithRetry(async () => {
        const metadata = await getMappingHistoryMetadata(singleSelectedSerial, controller.signal);
        if (!isValidPointTimeIndex(metadata)) {
          throw new Error("The map history service returned invalid metadata.");
        }
        const { displayLimit, pointCount, retainedFrom, retainedTo, snapshot } = metadata;
        if (pointCount <= 0 || retainedFrom === null || retainedTo === null || retainedFrom > retainedTo) {
          throw new Error("No retained map history is available.");
        }

        setDownloadProgress({ downloaded: 0, total: pointCount });
        return fetchAllMappingHistory({
          expectedPointCount: pointCount,
          fetchPage: (cursor) =>
            getMappingHistoryExportPage({
              serialNumber: singleSelectedSerial,
              from: retainedFrom,
              to: retainedTo,
              snapshot,
              pageSize: getSafeDisplayLimit(displayLimit),
              cursor,
              signal: controller.signal,
            }),
          onProgress: (downloaded) => {
            if (!controller.signal.aborted && requestId === exportRequestIdRef.current) {
              setDownloadProgress({ downloaded, total: pointCount });
            }
          },
        });
      });

      if (controller.signal.aborted || requestId !== exportRequestIdRef.current) {
        return;
      }

      const blob = createMapHistoryCsvBlob(completeHistory.device, completeHistory.dataPoints);
      downloadBlob(blob, `HPUE_Data_Points_${moment().format("YYYY-MM-DD_HH-mm-ss")}.csv`);
    } catch (error) {
      if (!controller.signal.aborted && requestId === exportRequestIdRef.current) {
        setDownloadError(error instanceof Error ? error.message : "Failed to download map history.");
      }
    } finally {
      if (requestId === exportRequestIdRef.current) {
        setIsDownloading(false);
      }
    }
  };

  const valueLabelFormat = (value: number) => moment(value).format("MM/DD/YY h:mm:ss A");
  const displayRouteTools = Boolean(singleSelectedSerial && historyBounds && sliderRange);
  const displayHelpRouteText = selectedSerials.length > 1;
  const maxSelectedPoints = historyMetadata ? getSafeDisplayLimit(historyMetadata.displayLimit) : 1;
  const formattedDownloadProgress = downloadProgress
    ? `${downloadProgress.downloaded.toLocaleString()} of ${downloadProgress.total.toLocaleString()} points`
    : undefined;

  return (
    <MapPageContainer>
      <PageTitle>Map</PageTitle>
      <PageContent>
        <div>
          <MapSidebar
            showDrawer={showDrawer}
            onCloseDrawer={() => setShowDrawer(false)}
            dataFieldStr={DATA_FIELDS_STR}
          />
        </div>

        <RightWrapper className={palette.mode}>
          {displayHelpRouteText && (
            <RouteToolsHelpContainer>
              <CommonText>
                Route tools can only be displayed when a single device is selected and contains mapped location points.
              </CommonText>
            </RouteToolsHelpContainer>
          )}
          {singleSelectedSerial && historyError && !displayRouteTools && (
            <RouteToolsHelpContainer>
              <ErrorText>{historyError}</ErrorText>
            </RouteToolsHelpContainer>
          )}

          <div id="map-container" style={{ height: "98.6%", width: "100%" }} />

          {displayRouteTools && historyBounds && sliderRange && (
            <RouteTools
              sliderRange={sliderRange}
              sliderBounds={historyBounds}
              pointTimes={historyMetadata?.pointTimes ?? []}
              maxSelectedPoints={maxSelectedPoints}
              onChange={handleSliderChange}
              onChangeCommitted={handleSliderCommit}
              toggleDeviceRoute={() => setShowRoute((current) => !current)}
              showRoute={showRoute}
              valueLabelFormat={valueLabelFormat}
              downloadMapHistory={downloadMapHistory}
              isWindowLoading={isWindowLoading}
              isDownloading={isDownloading}
              downloadProgress={formattedDownloadProgress}
              downloadError={downloadError ?? historyError}
            />
          )}
        </RightWrapper>
      </PageContent>
    </MapPageContainer>
  );
};

export default MapPage;
